import { getIn } from "@utils/index"
import * as R from "ramda"
import React from "react"

import {
  getBrands,
  getColors,
  getDeviceNameSlugMap,
  getDevices,
  getDiscountTiers,
  getSeries,
  getSubTypes,
  sortSeries,
} from "../Products/utils"

type Args = {
  clearanceData?: any
  customPrintProducts: any[]
  formattedProducts: any[]
  globalSettings?: any
}

const formatFilter = (arr, filters, handler) =>
  arr.map((obj) => ({
    ...obj,
    onClick: () => handler(obj),
    toggled: filters && R.includes(obj, filters),
  }))

const toggleFilter = (key, filter, filters = []) =>
  filters.length > 0 && R.includes(filter, filters)
    ? R.filter((filterObj: any) => filterObj[key] !== filter[key])
    : [...filters, filter]

/**
 * Manages all filter state for the Products view
 */
export function useProductsFilters({
  clearanceData,
  customPrintProducts,
  formattedProducts,
  globalSettings,
}: Args) {
  // Printed designs toggle
  const [showPrintedDesigns, setShowPrintedDesigns] = React.useState(false)
  const togglePrintedDesigns = () => {
    setShowPrintedDesigns(!showPrintedDesigns)
  }

  // Bundle products toggle
  const [showBundleProducts, setShowBundleProducts] = React.useState(false)
  const toggleBundleProducts = () => {
    setShowBundleProducts(!showBundleProducts)
  }

  const hasBundleProducts = React.useMemo(() => {
    return formattedProducts?.some((product) => Boolean(product.bundleProduct))
  }, [formattedProducts])

  // Magnet compatible toggle
  const [showMagnetCompatibleProducts, setShowMagnetCompatibleProducts] =
    React.useState(false)
  const toggleShowMagnetCompatibleProducts = () => {
    setShowMagnetCompatibleProducts(!showMagnetCompatibleProducts)
  }

  const hasMagnetCompatibleProducts = React.useMemo(() => {
    return formattedProducts?.some((product) => Boolean(product.builtInMagnet))
  }, [formattedProducts])

  // SubTypes
  const subTypes = React.useMemo(
    () => getSubTypes(formattedProducts as any),
    [formattedProducts]
  )

  const [subTypeFilters, setSubTypeFilters] = React.useState<any>([])
  const toggleSubType = (subType) => {
    setSubTypeFilters(toggleFilter("label", subType, subTypeFilters))
  }

  const formattedSubTypes = React.useMemo(
    () => formatFilter(subTypes, subTypeFilters, toggleSubType),
    [subTypes, subTypeFilters]
  )

  // Colors
  const colors = React.useMemo(
    () =>
      getColors(
        formattedProducts.filter((product) => !product.customPrint) as any
      ),
    [formattedProducts]
  )

  const [colorFilters, setColorFilters] = React.useState<any>([])
  const toggleColor = (color) => {
    setColorFilters(toggleFilter("label", color, colorFilters))
  }

  const formattedColors = React.useMemo(
    () => formatFilter(colors, colorFilters, toggleColor),
    [colors, colorFilters]
  )

  // Brands
  const brands = React.useMemo(
    () =>
      getBrands(
        formattedProducts as any,
        getIn(globalSettings, "menuProductBrandOrder", [])
      ),
    [formattedProducts, globalSettings]
  )

  const [brandFilters, setBrandFilters] = React.useState<any>([])
  const toggleBrand = (brand) => {
    setBrandFilters(toggleFilter("label", brand, brandFilters))
  }

  const formattedBrands = React.useMemo(
    () => formatFilter(brands, brandFilters, toggleBrand),
    [brands, brandFilters]
  )

  // Series
  const series = React.useMemo(() => {
    const productSeries = getSeries(formattedProducts as any)
    const seriesOrder = getIn(globalSettings, "menuProductSeriesOrder", [])
    return sortSeries(productSeries, R.map(R.prop("name"))(seriesOrder))
  }, [formattedProducts, globalSettings])

  const [seriesFilters, setSeriesFilters] = React.useState<any>([])
  const toggleSeries = (series) => {
    setSeriesFilters(toggleFilter("label", series, seriesFilters))
  }

  const formattedSeries = React.useMemo(
    () => formatFilter(series, seriesFilters, toggleSeries),
    [series, seriesFilters]
  )

  // Discount tiers
  const discountTiers = React.useMemo(() => {
    const discountFilters = getIn(clearanceData, "clearanceFilters", []).map(
      (d) => parseInt(d) / 5
    )
    const productDiscountTiers =
      getDiscountTiers(formattedProducts as any) || []
    return Object.keys(productDiscountTiers).reduce((tiers, tier) => {
      if (discountFilters.includes(parseInt(tier))) {
        tiers[tier] = productDiscountTiers[tier]
      }
      return tiers
    }, {})
  }, [clearanceData, formattedProducts])

  const [discountTierFilters, setDiscountTierFilters] = React.useState<any>([])
  const toggleDiscountTier = (tier) => {
    if (discountTierFilters[0] === tier) {
      setDiscountTierFilters([])
    } else {
      setDiscountTierFilters([tier])
    }
  }

  const formatDiscountTierLabel = (tier) => {
    const tiersCounts = Object.keys(discountTiers).filter(
      (t) => parseInt(t) >= parseInt(tier)
    )
    const totalCount = tiersCounts.reduce(
      (acc, curr) => acc + discountTiers[curr],
      0
    )

    if (tier === "0") {
      return `1% and more (${totalCount})`
    } else {
      return `${parseInt(tier) * 5}% and more (${totalCount})`
    }
  }

  const formattedDiscountTiers = React.useMemo(() => {
    const validDiscountTiers = R.omit(["-1"])(discountTiers)
    return Object.keys(validDiscountTiers).map(
      (tier) =>
        ({
          label: formatDiscountTierLabel(tier),
          onClick: () => {
            toggleDiscountTier(tier)
          },
          toggled: discountTierFilters && R.includes(tier, discountTierFilters),
        }) as any
    )
  }, [discountTiers, discountTierFilters])

  // Custom print devices
  const deviceNameSlugMap = React.useMemo(
    () => getDeviceNameSlugMap(customPrintProducts as any),
    [customPrintProducts]
  )

  const devices = React.useMemo(
    () => getDevices(customPrintProducts as any),
    [customPrintProducts]
  )

  const [devicesFilter, setDevicesFilter] = React.useState<any>([])
  const toggleDevices = (device) => {
    if (devicesFilter.length > 0 && devicesFilter[0].label === device.label) {
      setDevicesFilter([])
    } else {
      setDevicesFilter([device])
    }
  }

  const formattedDevices = React.useMemo(
    () => formatFilter(devices, devicesFilter, toggleDevices),
    [devices, devicesFilter]
  )

  // In stock / Clearance
  const [showInStock, setShowInStock] = React.useState(true)
  const toggleInStock = () => {
    setShowInStock(!showInStock)
  }

  const [showClearance, setShowClearance] = React.useState(false)
  const toggleClearance = () => {
    setShowClearance(!showClearance)
  }

  // Filter menu visibility
  const [showFilterMenu, setShowFilterMenu] = React.useState(false)
  const openFilterMenu = (e) => {
    e.stopPropagation()
    setShowFilterMenu(true)
  }
  const closeFilterMenu = () => {
    setShowFilterMenu(false)
  }

  // Reset all filters
  const resetFilters = () => {
    setShowPrintedDesigns(false)
    setShowMagnetCompatibleProducts(false)
    setSubTypeFilters([])
    setColorFilters([])
    setSeriesFilters([])
    setShowInStock(false)
    setShowClearance(false)
  }

  // Filter count
  const filterCount =
    (showPrintedDesigns ? 1 : 0) +
    (showBundleProducts ? 1 : 0) +
    (showMagnetCompatibleProducts ? 1 : 0) +
    subTypeFilters.length +
    colorFilters.length +
    brandFilters.length +
    seriesFilters.length +
    discountTierFilters.length +
    (showClearance ? 1 : 0) +
    (showInStock ? 1 : 0)

  return {
    brandFilters,
    closeFilterMenu,
    colorFilters,
    deviceNameSlugMap,
    devicesFilter,
    discountTierFilters,
    filterCount,
    formattedBrands,
    formattedColors,
    formattedDevices,
    formattedDiscountTiers,
    formattedSeries,
    formattedSubTypes,
    hasBundleProducts,
    hasMagnetCompatibleProducts,
    openFilterMenu,
    resetFilters,
    seriesFilters,
    showBundleProducts,
    showClearance,
    showFilterMenu,
    showInStock,
    showMagnetCompatibleProducts,
    showPrintedDesigns,
    subTypeFilters,
    toggleBundleProducts,
    toggleClearance,
    toggleInStock,
    togglePrintedDesigns,
    toggleShowMagnetCompatibleProducts,
  }
}
